import React from "react";
import { Link } from "react-router-dom";
import PolicyPage from "./PolicyPage";
import siteConfig from "../config/siteConfig";

const emailLink = (
  <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
);

const policyDefinitions = {
  payments: {
    kicker: "Orders & billing",
    title: "Payment & billing policy",
    intro: "How prices, payment authorization, billing checks and refunds work at checkout.",
    lead: "We show the order total before you authorize payment and use a hosted payment service so payment credentials are entered with the payment provider, not directly into our store.",
    sections: [
      {
        id: "payments-methods",
        title: "Accepted payment methods",
        content: <><p>Payment methods available for your device and delivery address are shown at checkout. Availability can change by country, currency, payment provider or transaction value. A displayed method is not guaranteed until the provider authorizes the transaction.</p><p>You confirm that you are authorized to use the selected method and that the billing information you submit is accurate.</p></>,
      },
      {
        id: "payments-pricing",
        title: "Prices, currency and charges",
        content: <><p>Store prices are displayed in {siteConfig.currency}. Product prices, discounts, estimated taxes and the {siteConfig.currencySymbol}{siteConfig.shippingFlatRate} tracked-delivery charge are itemized before payment. Your card issuer or wallet provider may apply foreign-exchange, cross-border or other charges that we do not control.</p><p>If an obvious pricing or calculation error occurs, we may pause or cancel the affected order and give you the choice to reorder at the correct price where permitted by law.</p></>,
      },
      {
        id: "payments-authorization",
        title: "Authorization and verification",
        content: <><p>A payment may be authorized, captured or temporarily held depending on the provider. We may request reasonable verification, limit quantities or decline a transaction when billing details do not match, authorization fails or fraud screening identifies unusual activity.</p><p>Declined-payment details are controlled by the issuer. Please contact the issuer first, then try another available method if appropriate.</p></>,
      },
      {
        id: "payments-security",
        title: "Payment data and security",
        content: <><p>Full card numbers and card security codes are handled by the hosted payment provider and are not stored in full by {siteConfig.brandName}. We may receive a transaction token, payment status, card brand, limited card digits and billing details needed to administer the order, prevent fraud and meet legal obligations.</p><p>Do not send card details by email or through the contact form. See our <Link to="/security">security policy</Link> for reporting suspicious activity.</p></>,
      },
      {
        id: "payments-refunds",
        title: "Refunds, reversals and disputes",
        content: <><p>Approved refunds are returned to the original payment method unless law requires another approach. Provider and bank processing times vary, and an authorization hold may disappear rather than appear as a separate credit.</p><p>Contact us before starting a chargeback so we can investigate. This does not limit any right you have to dispute a transaction with your provider. Return eligibility is governed by our <Link to="/returns">returns policy</Link>.</p></>,
      },
      {
        id: "payments-contact",
        title: "Billing support",
        content: <p>For a duplicate charge, refund-status question or billing mismatch, email {emailLink} with your order number and a description of the issue. Never include a full card number or security code.</p>,
      },
    ],
    related: [
      { label: "Orders", path: "/orders" },
      { label: "Returns", path: "/returns" },
      { label: "Security", path: "/security" },
    ],
  },
  orders: {
    kicker: "Orders & billing",
    title: "Order & cancellation policy",
    intro: "What happens after checkout, including acceptance, changes, cancellations and order limits.",
    lead: "We begin preparing paid orders quickly. Review names, quantities, variants and the delivery address before payment, and contact us promptly if something needs attention.",
    sections: [
      {
        id: "orders-confirmation",
        title: "Order receipt and acceptance",
        content: <><p>An on-screen or email acknowledgement confirms that we received your request; it does not by itself mean that the order has been accepted. Acceptance occurs when we confirm dispatch or otherwise expressly accept the order.</p><p>Please check the acknowledgement and tell us promptly if any detail is wrong. Keep the order number for future correspondence.</p></>,
      },
      {
        id: "orders-changes",
        title: "Changes and cancellations",
        content: <><p>Email {emailLink} as soon as possible to request an address, item or cancellation change. We cannot promise a change once fulfillment or payment processing has begun. We will explain the available return or delivery options if the request is too late.</p><p>For account security, we may ask you to confirm information associated with the order before making a change.</p></>,
      },
      {
        id: "orders-availability",
        title: "Availability and order limits",
        content: <><p>Products remain subject to availability even after checkout. We may limit quantities per customer, household, payment method or address to support fair access and deter unauthorized resale.</p><p>If an item becomes unavailable, we may cancel that item or the order and return any captured amount. We will not substitute a different product without your agreement.</p></>,
      },
      {
        id: "orders-decline",
        title: "When we may decline an order",
        content: <p>We may decline or cancel an order for failed payment, an obvious price or description error, unsupported delivery details, sanctions or trade restrictions, suspected fraud, unauthorized resale, abusive conduct or a legal requirement. Where appropriate, we will notify you and reverse any captured payment for the cancelled portion.</p>,
      },
      {
        id: "orders-address",
        title: "Address accuracy and delivery",
        content: <><p>You are responsible for providing a complete deliverable address and a reachable email address. Carrier rerouting is not always possible. Additional delivery or return-to-sender costs caused by incorrect details may be deducted or charged where permitted by law.</p><p>Delivery timing, tracking and missing-parcel procedures appear in our <Link to="/shipping">shipping policy</Link>.</p></>,
      },
      {
        id: "orders-records",
        title: "Order records and support",
        content: <p>We retain order and communication records as described in our <Link to="/privacy">privacy policy</Link>. For help, email {emailLink} from the address used at checkout and include the order number.</p>,
      },
    ],
    related: [
      { label: "Payments", path: "/payments" },
      { label: "Shipping", path: "/shipping" },
      { label: "Returns", path: "/returns" },
    ],
  },
  "product-safety": {
    kicker: "Product care",
    title: "Product information & safety",
    intro: "Practical guidance for choosing, storing and using personal-care products responsibly.",
    lead: "Product pages are general shopping information, not medical advice. The package you receive is the most current source for ingredients, warnings and directions.",
    sections: [
      {
        id: "safety-label",
        title: "Read the product label",
        content: <><p>Formulas, packaging, sizes and directions may be updated. Before each use, review the label on the item delivered and follow its directions and warnings. Do not use a product if the seal appears broken, the packaging is damaged or the contents seem altered.</p><p>Images and screen settings can make colors and textures appear different from the physical product.</p></>,
      },
      {
        id: "safety-use",
        title: "Safe use",
        content: <ul><li>Use only for the stated cosmetic purpose and avoid contact with eyes unless the label says otherwise.</li><li>Consider a small-area patch test before first use, especially if you have sensitive skin or known allergies.</li><li>Stop use if irritation or an unexpected reaction occurs; seek medical advice when symptoms are serious or persist.</li><li>Keep products out of reach of children and pets, and do not ingest products intended for external use.</li></ul>,
      },
      {
        id: "safety-ingredients",
        title: "Ingredients and sensitivities",
        content: <><p>Review the full ingredient list before purchase and again on the delivered label. “Natural,” “botanical” and similar descriptions do not mean allergy-free or suitable for every person. If you are pregnant, nursing, receiving medical care or managing a diagnosed condition, ask an appropriate health professional whether a product is suitable for you.</p><p>Contact us before ordering if you need help locating current ingredient information.</p></>,
      },
      {
        id: "safety-storage",
        title: "Storage and product life",
        content: <p>Store products tightly closed in a cool, dry place away from direct sunlight, extreme temperatures and contamination. Follow any expiry date or period-after-opening symbol on the package. Dispose of a product if its smell, color, consistency or container changes unexpectedly.</p>,
      },
      {
        id: "safety-issue",
        title: "Report a quality or safety issue",
        content: <p>Stop using the item and email {emailLink} with the order number, product name, batch or lot code, photographs and a description of the issue. For an urgent medical concern, contact local emergency or poison-control services first. See our <Link to="/quality-guarantee">quality policy</Link> for replacement and refund review.</p>,
      },
    ],
    related: [
      { label: "Quality", path: "/quality-guarantee" },
      { label: "Returns", path: "/returns" },
      { label: "Contact", path: "/contact" },
    ],
  },
  promotions: {
    kicker: "Offers",
    title: "Promotions & discount policy",
    intro: "The rules for promotional prices, discount codes, gifts and limited-time offers.",
    lead: "Each offer may include its own dates, products, locations or customer requirements. Those stated offer terms apply together with this policy.",
    sections: [
      {
        id: "promotions-eligibility",
        title: "Eligibility and duration",
        content: <p>Offers are valid only during the advertised period, while qualifying stock lasts and for eligible customers, products and delivery destinations. Time references use the time zone stated in the offer; if none is stated, the store operator's local time applies. We may request reasonable evidence of eligibility for restricted offers.</p>,
      },
      {
        id: "promotions-codes",
        title: "Using a discount code",
        content: <><p>Enter the code at checkout before payment. Unless the offer says otherwise, one code may be used per order, codes cannot be combined, and shipping, taxes, gift cards and excluded items do not count toward a minimum spend.</p><p>Codes have no cash value, cannot be sold or transferred for value, and cannot be applied after an order has been submitted.</p></>,
      },
      {
        id: "promotions-gifts",
        title: "Gifts and bundled offers",
        content: <p>A gift with purchase is available only while supplies last and may not be exchanged for cash. If a return causes the original order to fall below the qualifying threshold, the unused gift must also be returned or its stated value may be deducted from the refund where permitted by law. Bundled items may need to be returned together.</p>,
      },
      {
        id: "promotions-fair-use",
        title: "Fair use and errors",
        content: <p>We may reject, suspend or reverse a discount obtained through code sharing contrary to its terms, automated activity, account duplication, manipulation, unauthorized resale or an obvious publication or system error. If we cancel an affected paid order, we will return the captured amount for the cancelled portion.</p>,
      },
      {
        id: "promotions-returns",
        title: "Returns from a promotion",
        content: <p>Refunds are based on the amount actually paid for each returned item after the discount is allocated. Promotional pricing is not guaranteed for a replacement order placed after an offer ends. All other eligibility rules in our <Link to="/returns">returns policy</Link> continue to apply.</p>,
      },
    ],
    related: [
      { label: "Orders", path: "/orders" },
      { label: "Payments", path: "/payments" },
      { label: "Returns", path: "/returns" },
    ],
  },
  accessibility: {
    kicker: "Site information",
    title: "Accessibility statement",
    intro: "Our approach to making the rumnwillow shopping experience usable by more people.",
    lead: "We want customers with different abilities, devices and ways of navigating to be able to browse products, understand policies and complete a purchase.",
    sections: [
      {
        id: "accessibility-approach",
        title: "Our approach",
        content: <p>We use the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA as a reference point for ongoing improvement. This is a goal and working benchmark, not a claim that every page or third-party service currently conforms in every respect.</p>,
      },
      {
        id: "accessibility-features",
        title: "Accessibility features",
        content: <ul><li>Keyboard-accessible navigation and a skip-to-content link.</li><li>Visible focus indicators and descriptive labels for key controls.</li><li>Responsive layouts that support common screen sizes and zoom.</li><li>Semantic headings, form error messages and text alternatives for meaningful imagery where provided.</li></ul>,
      },
      {
        id: "accessibility-third-party",
        title: "Third-party content",
        content: <p>Checkout, payment, carrier tracking or linked content may be supplied by third parties. We do not control every aspect of those services, but we welcome reports about barriers encountered while using them in connection with an order.</p>,
      },
      {
        id: "accessibility-assistance",
        title: "Request assistance",
        content: <p>If a page, control or document is difficult to use, email {emailLink}. Tell us the page address, the task you were trying to complete, your browser or assistive technology if you are comfortable sharing it, and your preferred contact method. We will try to provide the information or service in another reasonable format.</p>,
      },
      {
        id: "accessibility-feedback",
        title: "Feedback and improvements",
        content: <p>Accessibility is an ongoing process. We review relevant feedback, prioritize barriers affecting core shopping tasks and update this statement when our approach materially changes. Accessibility feedback does not affect your eligibility to purchase or receive customer support.</p>,
      },
    ],
    related: [
      { label: "Contact", path: "/contact" },
      { label: "Privacy", path: "/privacy" },
      { label: "All policies", path: "/policies" },
    ],
  },
  "data-rights": {
    kicker: "Privacy",
    title: "Data rights & requests",
    intro: "How to ask about access, correction, deletion and other privacy choices.",
    lead: "Privacy rights differ by location. We will review each request under the law that applies and will not discriminate against you for exercising an applicable right.",
    sections: [
      {
        id: "data-rights-available",
        title: "Rights that may be available",
        content: <p>Depending on where you live, you may be able to request access to personal information, correction of inaccurate information, deletion, restriction, objection, portability, withdrawal of consent, or information about collection and disclosure. Some U.S. residents may also have rights concerning sale, sharing or targeted advertising. We do not currently sell personal information for money or use it for cross-context behavioral advertising.</p>,
      },
      {
        id: "data-rights-submit",
        title: "How to submit a request",
        content: <p>Email {emailLink} with the subject “Privacy request.” Describe the right you want to exercise and include the email address associated with your order or inquiry. Do not send identity documents unless we specifically request a secure method for them.</p>,
      },
      {
        id: "data-rights-verify",
        title: "Verification and authorized agents",
        content: <p>We may ask for information reasonably necessary to match the request to our records and protect information from unauthorized disclosure. An authorized agent may submit a request where local law allows, but we may ask for proof of authority and direct confirmation from the individual.</p>,
      },
      {
        id: "data-rights-limits",
        title: "Limits and retention",
        content: <p>A request may be limited or refused where an exception applies, including when information is needed to complete a transaction, detect security incidents, keep tax or legal records, exercise legal claims or protect another person's rights. We will explain the applicable reason when required.</p>,
      },
      {
        id: "data-rights-regional",
        title: "Regional privacy choices",
        content: <><p>Individuals in the United Kingdom, the EEA and certain other regions may have rights to access, correct or delete personal information, object to or restrict processing, request portability, or complain to a local supervisory authority. Residents of certain U.S. states may have an appeal or regulator-contact option.</p><p>These descriptions do not expand a law beyond its normal scope; our <Link to="/privacy">privacy policy</Link> explains the data practices to which a request relates.</p></>,
      },
      {
        id: "data-rights-marketing",
        title: "Marketing choices",
        content: <p>You may opt out of promotional email using its unsubscribe link or by contacting us. Transactional messages needed for an order, safety notice or requested support may still be sent. See our <Link to="/communications">communications policy</Link>.</p>,
      },
    ],
    related: [
      { label: "Privacy", path: "/privacy" },
      { label: "Cookies", path: "/cookies" },
      { label: "Communications", path: "/communications" },
    ],
  },
  security: {
    kicker: "Trust & safety",
    title: "Security & fraud policy",
    intro: "How we reduce misuse, respond to suspicious activity and help customers shop safely.",
    lead: "No online service can guarantee perfect security. We use proportionate safeguards and ask customers to use the store and payment services carefully.",
    sections: [
      {
        id: "security-safeguards",
        title: "Our safeguards",
        content: <p>We use administrative and technical measures designed to protect store and order information, limit access according to business need, monitor for misuse and use service providers for functions such as hosting and payment processing. Safeguards are reviewed as risks and services change.</p>,
      },
      {
        id: "security-payments",
        title: "Payments and fraud checks",
        content: <p>Payment credentials are entered through a hosted payment flow. We may use payment, device, address and order signals to identify unusual activity, request verification, delay fulfillment or decline an order. A fraud check is not an accusation and does not guarantee that all unauthorized activity will be detected.</p>,
      },
      {
        id: "security-customer",
        title: "Shop safely",
        content: <ul><li>Use a trusted device and network and keep browser software current.</li><li>Verify that messages and payment pages use the expected domain before entering information.</li><li>Never send a password, full card number or card security code by email.</li><li>Review order confirmations and payment statements promptly.</li></ul>,
      },
      {
        id: "security-phishing",
        title: "Suspicious messages",
        content: <p>We will not ask by email for a full payment-card number, card security code or remote access to your device. Do not open unexpected attachments or pay through an unverified link. Forward a suspicious message or screenshot to {emailLink} without including sensitive credentials.</p>,
      },
      {
        id: "security-report",
        title: "Report a security concern",
        content: <p>Email {emailLink} with a clear description, the affected page and steps to reproduce the issue. Do not access other people's data, disrupt service, use automated high-volume testing or publicly disclose details before we have had a reasonable chance to investigate. This is not a bug-bounty offer and does not authorize unlawful testing.</p>,
      },
      {
        id: "security-incidents",
        title: "Incident response",
        content: <p>If we confirm an incident involving personal information, we will contain and assess it, work with relevant service providers, preserve appropriate records and provide notices when required by applicable law. Our <Link to="/privacy">privacy policy</Link> explains how personal information is otherwise handled.</p>,
      },
    ],
    related: [
      { label: "Payments", path: "/payments" },
      { label: "Privacy", path: "/privacy" },
      { label: "Data rights", path: "/data-rights" },
    ],
  },
  communications: {
    kicker: "Privacy",
    title: "Communications policy",
    intro: "The messages we may send about orders, support, product safety and marketing choices.",
    lead: "We separate service messages needed to manage a request or purchase from optional promotional communications.",
    sections: [
      {
        id: "communications-service",
        title: "Service communications",
        content: <p>We may send order acknowledgements, payment status, dispatch and delivery updates, return information, responses to your questions, security messages and important product or policy notices. These messages are connected to a transaction or request and may continue even if you opt out of marketing.</p>,
      },
      {
        id: "communications-marketing",
        title: "Promotional communications",
        content: <p>Where offered and permitted, promotional email is sent based on your request, consent or another lawful basis. An invitation to subscribe will explain what you are signing up for. We do not require marketing consent as a condition of completing an ordinary purchase.</p>,
      },
      {
        id: "communications-choice",
        title: "Unsubscribe and preferences",
        content: <p>Use the unsubscribe link in a promotional email or contact {emailLink}. Processing can take a short period, so you may receive a message already queued. Unsubscribing from marketing does not stop essential order or customer-care messages.</p>,
      },
      {
        id: "communications-delivery",
        title: "Delivery and accuracy",
        content: <p>You are responsible for providing a current email address and checking filtered or junk folders. We cannot guarantee delivery by an email or network provider. Never rely only on email for an urgent health or safety issue.</p>,
      },
      {
        id: "communications-safety",
        title: "Recognizing our messages",
        content: <p>We will not ask by email for a full card number, card security code or account password. If a message appears suspicious, do not use its links; contact us through the address published on this website and review our <Link to="/security">security policy</Link>.</p>,
      },
    ],
    related: [
      { label: "Privacy", path: "/privacy" },
      { label: "Data rights", path: "/data-rights" },
      { label: "Security", path: "/security" },
    ],
  },
  "intellectual-property": {
    kicker: "Legal",
    title: "Intellectual property policy",
    intro: "Permitted use of rumnwillow names, content, product imagery and website materials.",
    lead: "Original store content and brand assets are protected by applicable intellectual-property laws. Respect for third-party rights is part of the same policy.",
    sections: [
      {
        id: "ip-ownership",
        title: "Ownership and licenses",
        content: <p>The {siteConfig.brandName} name, logos, original copy, graphics, photographs, product presentation, layout and software are owned by us or used under permission, except where another owner is identified. Access to the store does not transfer ownership or grant an implied commercial license.</p>,
      },
      {
        id: "ip-permitted",
        title: "Permitted personal use",
        content: <p>You may view the store, save ordinary order records and print a reasonable number of policy or purchase pages for personal, non-commercial use. Any legal notice contained in the material must be kept intact.</p>,
      },
      {
        id: "ip-restricted",
        title: "Restricted use",
        content: <p>Without written permission, you may not copy product media or substantial text, use our marks in a domain or advertisement, imply sponsorship, create confusingly similar branding, scrape or republish the catalog, train a commercial model on protected store content, remove rights notices or sell derivative materials. Exceptions required by law remain available.</p>,
      },
      {
        id: "ip-third-party",
        title: "Third-party rights",
        content: <p>Third-party names, payment marks and service logos remain the property of their respective owners and are used only to identify compatible services. Their appearance does not transfer rights or necessarily imply endorsement.</p>,
      },
      {
        id: "ip-notice",
        title: "Rights concerns and notices",
        content: <p>If you believe material on the store infringes your copyright, trademark or other right, email {emailLink} with your contact details, a description of the protected work, the exact page and material at issue, the basis for your claim and a good-faith confirmation that your notice is accurate. We may ask for additional information and will review the notice without promising a particular outcome.</p>,
      },
    ],
    related: [
      { label: "Terms", path: "/terms" },
      { label: "Complaints", path: "/complaints" },
      { label: "All policies", path: "/policies" },
    ],
  },
  sustainability: {
    kicker: "Responsible care",
    title: "Sustainability & packaging",
    intro: "How we communicate environmental information and what to know about order packaging.",
    lead: "Environmental impacts are complex, and disposal systems vary. We aim to use specific, supportable descriptions instead of broad guarantees.",
    sections: [
      {
        id: "sustainability-claims",
        title: "Environmental claims",
        content: <p>Terms such as “recyclable,” “recycled content” or “plastic-conscious” describe a particular material or packaging choice where stated; they do not mean a product or order has no environmental impact. Availability of collection and recycling depends on local facilities and contamination rules.</p>,
      },
      {
        id: "sustainability-packaging",
        title: "Order packaging",
        content: <p>We balance material reduction with the need to protect products during delivery. An order may include paper, protective fill, seals or other materials required for hygiene, leakage prevention or carrier handling. Packaging can change as suppliers, testing and local requirements change.</p>,
      },
      {
        id: "sustainability-disposal",
        title: "Reuse, recycling and disposal",
        content: <p>Empty and clean components where safe, separate materials when required, and follow the instructions of your local collection service. Pumps, mixed-material parts, mirrors, small components and items with product residue may need special disposal. Do not rinse a product into waterways contrary to local guidance.</p>,
      },
      {
        id: "sustainability-delivery",
        title: "Delivery choices",
        content: <p>Combining items in one order can reduce separate parcels, but fulfillment needs may occasionally require split delivery. Delivery estimates and charges are covered by our <Link to="/shipping">shipping policy</Link>.</p>,
      },
      {
        id: "sustainability-feedback",
        title: "Questions and feedback",
        content: <p>For information about a specific package component or to flag an unclear claim, email {emailLink} with the product or order details. We may update descriptions when materials or better evidence become available.</p>,
      },
    ],
    related: [
      { label: "Shipping", path: "/shipping" },
      { label: "Product safety", path: "/product-safety" },
      { label: "Contact", path: "/contact" },
    ],
  },
  complaints: {
    kicker: "Customer care",
    title: "Complaints & resolution policy",
    intro: "How to raise a concern about an order, product, payment, privacy matter or store experience.",
    lead: "We want a fair opportunity to understand and address concerns. This process does not replace or limit any non-waivable consumer, privacy or payment right.",
    sections: [
      {
        id: "complaints-contact",
        title: "Start with customer care",
        content: <p>Email {emailLink} with your order number, the email used at checkout, a clear description, relevant dates and the outcome you are requesting. For damaged or incorrect goods, attach photographs where practical and keep the item and packaging while we review the issue.</p>,
      },
      {
        id: "complaints-review",
        title: "How we review a complaint",
        content: <p>We may check order, payment, delivery and correspondence records, ask focused follow-up questions and consult the relevant service provider. We aim to acknowledge and handle complaints within a reasonable time, but complex carrier, payment or safety investigations may take longer.</p>,
      },
      {
        id: "complaints-outcomes",
        title: "Possible outcomes",
        content: <p>Depending on the facts and applicable policy, an outcome may include an explanation, correction, replacement, eligible refund, store-process improvement or a decision that the requested remedy is not available. Any refund or return remains subject to applicable law and the relevant published policy.</p>,
      },
      {
        id: "complaints-escalation",
        title: "Escalation",
        content: <p>If you disagree with the first response, reply with the reason and any new evidence and ask for a second review. You may also have a right to contact a consumer, privacy or payment authority in your location. Nothing in this process requires you to give up that right.</p>,
      },
      {
        id: "complaints-conduct",
        title: "Respectful communication",
        content: <p>We may limit a channel or end repetitive contact that is threatening, discriminatory, unlawful, knowingly false or abusive, while preserving access to a reasonable method for a genuine unresolved matter. We will not disadvantage a customer merely for making a good-faith complaint.</p>,
      },
    ],
    related: [
      { label: "Contact", path: "/contact" },
      { label: "Returns", path: "/returns" },
      { label: "Data rights", path: "/data-rights" },
    ],
  },
  "quality-guarantee": {
    kicker: "Product care",
    title: "Quality guarantee policy",
    intro: "What to do if a product arrives damaged, incorrect, defective or appears unsafe.",
    lead: "We review genuine product and fulfillment issues individually and provide an appropriate remedy where the claim is verified.",
    sections: [
      {
        id: "quality-inspect",
        title: "Inspect your order",
        content: <p>Check the parcel soon after delivery. If an item is missing, incorrect, leaking, broken, unsealed or materially different from what was ordered, stop using it and contact us within 7 days where practical. A delay does not remove any longer mandatory right provided by law.</p>,
      },
      {
        id: "quality-evidence",
        title: "Information we may need",
        content: <p>Email {emailLink} with the order number, product name, quantity affected, batch or lot code, a description and clear photographs of the product, label, shipping box and damage. Keep the item and packaging until we confirm whether return or disposal is appropriate.</p>,
      },
      {
        id: "quality-remedy",
        title: "Assessment and remedy",
        content: <p>We may compare fulfillment records, request further images or arrange a return. If the issue is verified, the available remedy may be replacement, repair where relevant, refund or another remedy required by law. Availability, location and safety considerations can affect the appropriate option.</p>,
      },
      {
        id: "quality-exclusions",
        title: "What is not a product defect",
        content: <p>Normal scent, color or texture variation, a personal preference, misuse, unsuitable storage, ordinary wear, use contrary to the label, damage after delivery or purchase from an unauthorized seller is not normally a defect. This does not exclude a verified manufacturing or safety issue.</p>,
      },
      {
        id: "quality-rights",
        title: "Consumer rights and safety",
        content: <p>This policy is in addition to mandatory consumer guarantees and does not limit rights that cannot lawfully be waived. For a suspected adverse reaction or safety concern, stop use and follow our <Link to="/product-safety">product safety guidance</Link>; seek urgent professional help when needed.</p>,
      },
    ],
    related: [
      { label: "Product safety", path: "/product-safety" },
      { label: "Returns", path: "/returns" },
      { label: "Complaints", path: "/complaints" },
    ],
  },
};

export const additionalPolicyMeta = [
  { key: "payments", label: "Payment & billing", path: "/payments", description: "Accepted methods, authorization, pricing, security and refunds." },
  { key: "orders", label: "Orders & cancellations", path: "/orders", description: "Order acceptance, changes, cancellations, limits and address accuracy." },
  { key: "product-safety", label: "Product information & safety", path: "/product-safety", description: "Labels, ingredients, safe use, storage and issue reporting." },
  { key: "quality-guarantee", label: "Quality guarantee", path: "/quality-guarantee", description: "Damaged, incorrect, defective or potentially unsafe products." },
  { key: "promotions", label: "Promotions & discounts", path: "/promotions", description: "Offer eligibility, discount codes, gifts, errors and returns." },
  { key: "data-rights", label: "Data rights & requests", path: "/data-rights", description: "Access, correction, deletion and regional privacy choices." },
  { key: "communications", label: "Communications", path: "/communications", description: "Order messages, optional marketing and unsubscribe choices." },
  { key: "security", label: "Security & fraud", path: "/security", description: "Payment safety, fraud checks, suspicious messages and reporting." },
  { key: "accessibility", label: "Accessibility", path: "/accessibility", description: "Accessibility approach, features, assistance and feedback." },
  { key: "intellectual-property", label: "Intellectual property", path: "/intellectual-property", description: "Permitted content use, brand rights and infringement notices." },
  { key: "sustainability", label: "Sustainability & packaging", path: "/sustainability", description: "Environmental claims, packaging and disposal guidance." },
  { key: "complaints", label: "Complaints & resolution", path: "/complaints", description: "How concerns are reviewed, resolved and escalated." },
];

function AdditionalPolicy({ policyKey }) {
  const policy = policyDefinitions[policyKey];

  if (!policy) return null;

  return <PolicyPage {...policy} />;
}

export default AdditionalPolicy;
